'use client';

import { Box, HStack } from '@terra/ui-styled-system';
import { ArrowUp02Icon, Tick01Icon, Copy01Icon } from '@terra/ui-icons';
import { useCallback, useState } from 'react';
import { useTranslations } from 'next-intl';
import { Badge } from '../../../../../components/badge';
import { IconButton } from '../../../../../components/icon-button';
import { Text } from '../../../../../components/text';
import { WebSocketExecutionShell } from './WebSocketExecutionShell';
import type { WebSocketConnectView } from './parse-websocket-execution-view';

type WebSocketConnectedViewProps = {
  view: WebSocketConnectView;
};

export function WebSocketConnectedView({ view }: WebSocketConnectedViewProps) {
  const t = useTranslations('designSystem.chat.websocketClient');
  const tChat = useTranslations('designSystem.chat');
  const [copied, setCopied] = useState(false);
  const handleCopy = useCallback(() => {
    if (!view.sessionId) {
      return;
    }

    void navigator.clipboard.writeText(view.sessionId);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [view.sessionId]);

  return (
    <WebSocketExecutionShell
      mode="connect"
      testId="websocket-execution-connect"
      error={view.error}
      status={
        <Badge size="sm" variant="fill">
          {view.error ? tChat('failed') : t('connected')}
        </Badge>
      }
    >
      {view.url ? (
        <HStack gap={1} alignItems="flex-start">
          <Text fontSize="xs" color="fg.muted" flexShrink={0}>
            {t('endpoint')}
          </Text>
          <Text
            fontSize="xs"
            fontFamily="mono"
            color="fg.default"
            wordBreak="break-all"
          >
            {view.url}
          </Text>
        </HStack>
      ) : null}
      {view.sessionId ? (
        <HStack gap={1} justifyContent="space-between">
          <HStack gap={1} flex="1" minW={0}>
            <Text fontSize="xs" color="fg.muted" flexShrink={0}>
              {t('sessionId')}
            </Text>
            <Text
              fontSize="xs"
              fontFamily="mono"
              color="fg.subtle"
              wordBreak="break-all"
            >
              {view.sessionId}
            </Text>
          </HStack>
          <IconButton
            variant="ghost"
            size="xs"
            aria-label={t('copySessionId')}
            onClick={handleCopy}
            flexShrink={0}
          >
            {copied ? <Tick01Icon size={12} /> : <Copy01Icon size={12} />}
          </IconButton>
        </HStack>
      ) : null}
      {view.authenticated ? (
        <HStack gap={1.5} alignItems="center">
          <ArrowUp02Icon size={10} color="var(--colors-fg-muted)" />
          <Text fontSize="xs" color="fg.muted">
            {t('authenticated')}
          </Text>
        </HStack>
      ) : null}
      {view.initialMessage ? (
        <Box p={2} bg="bg.muted" borderRadius="sm" width="100%">
          <Text
            fontSize="xs"
            fontFamily="mono"
            color="fg.subtle"
            whiteSpace="pre-wrap"
            wordBreak="break-all"
          >
            {view.initialMessage}
          </Text>
        </Box>
      ) : null}
      {!view.error ? (
        <Text fontSize="xs" color="fg.muted">
          {t('connectedHint')}
        </Text>
      ) : null}
    </WebSocketExecutionShell>
  );
}
